export const recaptchaSiteKey =
  (process.env.PUBLIC_RECAPTCHA_V2_SITE_KEY || "").trim();

let recaptchaScriptPromise: Promise<void> | null = null;

function isRecaptchaReady() {
  const grecaptcha = window.grecaptcha as Partial<Grecaptcha> | undefined;
  return (
    typeof grecaptcha?.render === "function" &&
    typeof grecaptcha?.ready === "function"
  );
}

function waitForRecaptchaReady(timeoutMs = 5000) {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (isRecaptchaReady()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      if (isRecaptchaReady()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Timed out waiting for reCAPTCHA to initialize"));
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });
}

export function loadRecaptchaScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (isRecaptchaReady()) {
    return Promise.resolve();
  }

  if (recaptchaScriptPromise) {
    return recaptchaScriptPromise;
  }

  recaptchaScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha-script="true"]',
    );

    const handleLoad = () => {
      void waitForRecaptchaReady().then(resolve).catch(reject);
    };

    const handleError = () => {
      reject(new Error("Failed to load reCAPTCHA"));
    };

    if (existingScript) {
      void waitForRecaptchaReady().then(resolve).catch(reject);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaScript = "true";
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.head.append(script);
  }).catch((error) => {
    recaptchaScriptPromise = null;
    throw error;
  });

  return recaptchaScriptPromise;
}
